import React, { useMemo } from 'react'
import { MapContainer, Marker, Popup, TileLayer, Tooltip } from 'react-leaflet'
import { ATTRIBUTION, ATTRIBUTION_STADIA, DEFAULT_ZOOM } from '../../constants'
import { ICON, DEFAULT_CENTER } from '../../constants'
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography, useTheme } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTranslation } from 'react-i18next'


const getAssetsAndBounds = (assetList) => {
  const bounds = [], assetsWithLocation = [], assetsWithoutLocation = []

  for (let asset of assetList) {
    if (!asset.location_bags) {
      assetsWithoutLocation.push(asset)
      continue
    }
    try {
      const location = JSON.parse(asset.location_bags)
      if (!location.geometry || !location.geometry.coordinates) {
        assetsWithoutLocation.push(asset)
        continue
      }
      const bound = [location.geometry.coordinates[1], location.geometry.coordinates[0]]
      bounds.push(bound)
      assetsWithLocation.push({
        id: asset.id,
        position: bound
      })
    } catch (error) {
      assetsWithoutLocation.push(asset)
    }
  }

  return {
    bounds,
    assetsWithLocation,
    assetsWithoutLocation
  }
}

export const MapAssets = ({ 
  assetList,
  style = { 
    height: 400,
    maxHeight: 800,
    width: '100%',
  }
}) => {
  
  const { bounds, assetsWithLocation, assetsWithoutLocation } = useMemo(() => getAssetsAndBounds(assetList), [assetList])
  const { t } = useTranslation()
  const theme = useTheme()

  const renderMarkers = () => {
    if(!assetsWithLocation) return null
    return assetsWithLocation.map(({ id, position }) => (
      <Marker 
        icon={ICON}
        key={id} 
        position={position}
      >
        <Popup>{t('component.mapAsset.assetId', { id })}</Popup>
        <Tooltip sticky>{t('component.mapAsset.assetId', { id })}</Tooltip>
      </Marker>
    ))
  }

  const renderAssetsWithoutLocation = () => {
    if(!assetsWithoutLocation || assetsWithoutLocation.length === 0) return null
    const shouldRenderAccordion = assetsWithoutLocation.length > 10
    
    const renderAssetsIds = () => {
      return (
        <Box sx={{ display: 'flex', flexFlow: 'wrap', width: '100%', gap: 1, maxHeight: 100, overflowY: 'auto', p: 2 }}>
          {!shouldRenderAccordion && t('component.mapAsset.assetsWithoutLocation')+':'}
          {assetsWithoutLocation.map((asset, index) => {
            return (
              <Box
                sx={{ backgroundColor: theme?.palette?.primary?.main, fontSize: '0.8rem', padding: '0.3rem', borderRadius: '0.5rem' }}
                key={index}
              >
                <Typography variant='inherit' color={'primary.contrastText'}>{asset.id}</Typography>
              </Box>
            )
          })}
        </Box>
      )
    }

    if (shouldRenderAccordion)
      return (
        <Box sx={{mt: 2}}>
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="asset-map-content"
              id="asset-map-header"
            >
              {t('component.mapAsset.assetsWithoutLocation')}
            </AccordionSummary>
            <AccordionDetails>
              {renderAssetsIds()}
            </AccordionDetails>
          </Accordion>
        </Box>
      )

    return renderAssetsIds()
  }

  const mapProps = useMemo(() => {
    if (!assetList || assetList.length === 0 || bounds.length === 0) {
      return {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
      }
    }
    return {
      bounds: bounds,
      zoom: 3,
    }
  }, [assetList, bounds])

  return (
    <>
      <MapContainer
        {...mapProps}
        maxZoom={17}
        scrollWheelZoom
        style={style}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        />
        <TileLayer
          attribution={ATTRIBUTION}
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        />
        <TileLayer
          attribution={ATTRIBUTION_STADIA}
          url="https://tiles.stadiamaps.com/tiles/stamen_toner_labels/{z}/{x}/{y}{r}.{ext}"
          subdomains={'abcd'}
          minZoom={0}
          maxZoom={20}
          ext={'png'}
        />
        {renderMarkers()}
      </MapContainer>
      {renderAssetsWithoutLocation()}
      {(!assetList || assetList.length === 0) && 
        <Typography py={2} textAlign={'center'}>{t("component.mapAsset.noData")}</Typography>
      }
    </>
  )
}
export default MapAssets